/**
 * Boundary
 * 
 * @Author: Focci
 * @Date: 2023-07-17 16:07:44
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-17 16:07:44
 */

import PropTypes from 'prop-types'
import { polygon } from '@turf/turf'
import { Source, Layer, useMap } from 'react-map-gl'
import { useMemo, useId, useEffect, useState, useCallback } from 'react'
import { objectCoordToArray } from '@lib/utils'

export function CircleBoundary({
  center = [174.76081567, -36.86834371],
  radius = 5, // km
}) {
  const id = useId()
  const { current } = useMap()
  
  const [r, setRadius] = useState(0)

  const calculateRadius = useCallback((zoom) => {
    const scale = 2 ** zoom
    const metersPerPixel = (156543.03392 * Math.cos((center[1] * Math.PI) / 180)) / scale
    const radiusInMeters = radius * 2 * 1000

    return radiusInMeters / metersPerPixel
  }, [center, radius])

  useEffect(() => {
    const mp = current.getMap()
    const initial = () => setRadius(calculateRadius(mp.getZoom()))

    initial()
    mp.on('zoom', initial)

    return () => mp.off('zoom', initial)
  }, [calculateRadius, current, id])

  return (
    <Source 
      type="geojson" 
      id={id}
      data={{
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: center
          },
          properties: {}
        }]
      }}
    >
      <Layer 
        source={id}
        type="circle"
        paint={{
          'circle-radius': r,
          'circle-color': 'rgba(255, 133, 0, 0.1)',
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255, 133, 0, 1)'
        }}
      />
    </Source>
  )
}

export default function Boundary({
  data = [], // [[{lng: 174.76081567, lat: -36.86834371}, ...]]
  translate = true, // 是否需要转换数据
  color = '#252831',
  borderColor,
  linePaint = null,
  needFill = false, // 是否有内部填充
  fillPaint = null
}) {
  const id = useId()
  const boundary = useMemo(() => {
    let res = []
    try{
      const coods = translate ? objectCoordToArray(data) : data
      res = coods.length > 0 ? polygon(coods) : []
    } catch(err) { /**/ }
    
    return res
  }, [data, translate])

  if(boundary.length === 0) {
    return null
  }
  
  return (
    <Source 
      id={id}
      type="geojson" 
      data={boundary}
    >
      <Layer 
        type="line" 
        source={id}
        paint={{ 
          'line-width': 2, 
          'line-color': borderColor || color,
          ...linePaint,
        }}
      />
      {needFill && (
        <Layer 
          type="fill" 
          source={id}
          paint={{ 
            'fill-opacity': 0.1,
            'fill-color': color,
            ...fillPaint,
          }}
        />
      )}
    </Source>
  )
}

Boundary.propTypes = {
  data: PropTypes.array,
  color: PropTypes.string,
  linePaint: PropTypes.object,
  fillPaint: PropTypes.object,
}
